import { Fragment, useMemo, useState, useRef, useEffect } from 'react';

import { usePrismicProductPageQuery } from '@prismic/gqlTypes/prismicHooks';
import { getTextureThumbnailUrl } from '@temp/core/utils';
import { translate } from '@utils/translations';

import { useMediaQuery } from '@hooks/useMediaQuery';
import { smMediumScreen } from '@styles/constants';

import upperFirst from 'lodash/upperFirst';

import { getAttributeChoiceTypeFieldComponent } from '@components/molecules/AttributeFieldTypes';

import {
  ColorSamplesButton,
  ColorSampleSlice,
} from '@components/molecules/ColorSamplesButton';

import {
  ProductAttributeButton,
  ProductAttributesType,
} from '@components/molecules/ProductAttributeButton';

import { attributesSorter, attributesMobileSorter } from './utils';

import { IProductVariantPickerProps } from './types';
import * as S from './styles';

export const ProductVariantPicker: React.FC<IProductVariantPickerProps> = ({
  attributes,
  setActiveImage,
  isSampleType,
  productType,
}) => {
  const [isSmallScreen] = useMediaQuery(`(max-width: ${smMediumScreen}px)`);

  const { data } = usePrismicProductPageQuery({
    variables: {
      productUid: productType,
    },
  });

  const colorSample = useMemo(() => {
    const { product: prismicProduct } = data || {};
    return (
      prismicProduct?.body.filter((slice) => slice.type === 'color_sample') ||
      []
    );
  }, [data]);

  const productAttributes = useMemo(() => {
    const { product: prismicProduct } = data || {};
    return prismicProduct?.attributes || [];
  }, [data]);

  // Ensure we scroll the tab container to expose all attributes
  const tabWrapper = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => {
    if (tabWrapper) {
      const activeWrapper = tabWrapper.current.children[activeIndex];
      const activeWidth = activeWrapper.offsetWidth;
      const activeLeft = activeWrapper.offsetLeft;
      const scrollWidth = tabWrapper.current.offsetWidth;
      const scrollLeft = tabWrapper.current.offsetLeft;
      const scrollLeftPosition =
        activeLeft - scrollLeft - scrollWidth / 2 + activeWidth / 2;

      tabWrapper.current.scrollLeft = scrollLeftPosition;
    }
  }, [activeIndex]);

  const sortedAttributes = useMemo(
    () =>
      isSmallScreen
        ? attributes.sort(attributesMobileSorter)
        : attributes.sort(attributesSorter),
    [attributes, isSmallScreen]
  );

  return (
    <S.Wrapper>
      <S.TabList ref={tabWrapper}>
        {sortedAttributes.map((item, i) => (
          <S.Tab
            active={activeIndex === i}
            key={`tab-${item.attribute.id}`}
            onClick={() => setActiveIndex(i)}
            onKeyPress={() => setActiveIndex(i)}
            role="button"
            tabIndex={0}
          >
            {!isSampleType &&
              upperFirst(translate(item.attribute, 'name')).replace('-', ' ')}
          </S.Tab>
        ))}
      </S.TabList>
      <S.TabContent />
      {sortedAttributes.map((item, i) => {
        const AttributeField = getAttributeChoiceTypeFieldComponent(
          item.attribute.choiceType
        );
        const productAttribute = productAttributes.find(
          (pa) => pa.attribute_slug === item.attribute.slug
        );
        return (
          <Fragment key={item.attribute.id}>
            <S.Item
              key={`content-${item.attribute.id}`}
              hide={activeIndex !== i}
            >
              {!isSampleType && (
                <S.Name key={`name-${item.attribute.id}`}>
                  {upperFirst(translate(item.attribute, 'name')).replace(
                    '-',
                    ' '
                  )}
                  :
                </S.Name>
              )}
              <AttributeField
                attribute={item}
                squareOptions={isSampleType}
                onOptionHover={(option) => {
                  setActiveImage(
                    // texture as a fallback
                    option?.image?.url || getTextureThumbnailUrl(option?.slug)
                  );
                }}
              />
            </S.Item>
            {item.attribute.slug.includes('door-color') &&
              colorSample.length > 0 && (
                <S.Item
                  key="content-color-samples-button"
                  hide={activeIndex !== i}
                >
                  <ColorSamplesButton
                    colorSample={colorSample as unknown as ColorSampleSlice[]}
                  />
                </S.Item>
              )}
            {productAttribute && (
              <S.Item hide={activeIndex !== i}>
                <ProductAttributeButton
                  key={`content-${productAttribute.attribute_slug}-button`}
                  attribute={
                    productAttribute.attribute_content as unknown as ProductAttributesType
                  }
                />
              </S.Item>
            )}
          </Fragment>
        );
      })}
    </S.Wrapper>
  );
};

ProductVariantPicker.displayName = 'ProductVariantPicker';
